import { useEffect, useState } from "react";
import type { DocumentDetail, FileInfo, ProjectDetail, ProjectSummary } from "@handvero/shared";
import { api, errorText } from "../api";
import { Sheet } from "./ui";

/** Dateien aus den eigenen Projektordnern auswählen und an einen Beleg hängen (Fotos und PDF). */
export function ProjectFilePicker(props: { documentId: string; projectId: string | null; onClose: () => void; onDone: (doc: DocumentDetail) => void }) {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [projectId, setProjectId] = useState<string>(props.projectId ?? "");
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [folderId, setFolderId] = useState("");
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get<ProjectSummary[]>("/api/projects").then(setProjects, (e) => setError(errorText(e)));
  }, []);
  useEffect(() => {
    setProject(null);
    setFolderId("");
    setFiles([]);
    if (!projectId) return;
    api.get<ProjectDetail>(`/api/projects/${projectId}`).then(setProject, (e) => setError(errorText(e)));
  }, [projectId]);
  useEffect(() => {
    setFiles([]);
    if (!folderId) return;
    api.get<FileInfo[]>(`/api/folders/${folderId}/files`).then((f) => setFiles(f.filter((x) => x.kind === "photo" || x.kind === "pdf")), (e) => setError(errorText(e)));
  }, [folderId]);

  function toggle(id: string) {
    setChosen((c) => {
      const n = new Set(c);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  async function attach() {
    setBusy(true);
    setError("");
    try {
      props.onDone(await api.post<DocumentDetail>(`/api/documents/${props.documentId}/attachments/from-files`, { fileIds: [...chosen] }));
      props.onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Sheet title="Datei aus einem Projekt wählen" onClose={props.onClose}>
      <div className="cu-form">
        <p className="muted-s wide">Wählen Sie Projekt und Ordner, dann die Fotos oder PDFs. Die Dateien werden kopiert, im Projektordner bleibt alles, wie es ist.</p>
        <label className="wide">
          <span>Projekt</span>
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">Bitte wählen …</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        {project && (
          <label className="wide">
            <span>Ordner</span>
            <select value={folderId} onChange={(e) => setFolderId(e.target.value)}>
              <option value="">Bitte wählen …</option>
              {project.folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name} ({f.fileCount})
                </option>
              ))}
            </select>
          </label>
        )}
        {folderId && files.length === 0 && <p className="muted-s wide">In diesem Ordner liegen keine Fotos oder PDFs.</p>}
        {files.length > 0 && (
          <ul className="pfp-grid wide">
            {files.map((f) => (
              <li key={f.id}>
                <label className={chosen.has(f.id) ? "on" : undefined}>
                  <input type="checkbox" checked={chosen.has(f.id)} onChange={() => toggle(f.id)} />
                  {f.hasThumb ? <img src={`/api/files/${f.id}/thumb`} alt="" loading="lazy" /> : <span className="pfp-pdf">PDF</span>}
                  <small>{f.name}</small>
                </label>
              </li>
            ))}
          </ul>
        )}
        {error && <p className="wide" role="alert" style={{ color: "var(--danger, #b3261e)" }}>{error}</p>}
        <div className="cu-buttons wide">
          <button type="button" className="dbtn ghost" onClick={props.onClose}>
            Abbrechen
          </button>
          <button type="button" className="dbtn" disabled={busy || chosen.size === 0} onClick={() => void attach()}>
            {chosen.size ? `${chosen.size} ${chosen.size === 1 ? "Datei" : "Dateien"} anhängen` : "Anhängen"}
          </button>
        </div>
      </div>
    </Sheet>
  );
}
